import React, { useRef, useState } from 'react';
import { Camera, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { uploadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getProfilePictureOrInitials } from '../utils/profilePicture';
import { toast } from 'sonner';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024;

const ProfilePictureUploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const { member, user, updateMember } = useAuth();
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const displayName = member?.individual_name || user?.full_name || 'Member';

  const clearSelection = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen && !uploading) clearSelection();
    onOpenChange(nextOpen);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, GIF, or WebP image.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error('Profile picture must be less than 5MB.');
      event.target.value = '';
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      inputRef.current?.click();
      return;
    }
    try {
      setUploading(true);
      const response = await uploadsApi.uploadProfilePicture(selectedFile);
      const profilePicture = response.data?.profile_picture;
      if (!profilePicture) throw new Error('Profile picture URL was not returned');

      const updatedMember = { ...member, profile_picture: profilePicture };
      updateMember(updatedMember);
      onUploaded?.(profilePicture);
      toast.success('Profile picture uploaded successfully.');
      clearSelection();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload Profile Picture</DialogTitle>
          <DialogDescription>
            Add a personal photo to your member profile. Your company logo will remain unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex justify-center">
            <img
              src={previewUrl || getProfilePictureOrInitials(member, displayName)}
              alt="Profile preview"
              className="h-36 w-36 rounded-full border-4 border-blue-100 object-cover"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            <Camera size={16} className="mr-2" />
            {selectedFile ? 'Choose Another Photo' : 'Choose Photo'}
          </Button>
          <p className="text-center text-xs text-gray-500">JPEG, PNG, GIF, or WebP. Maximum 5MB.</p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={uploading}>
            Not Now
          </Button>
          <Button type="button" onClick={handleUpload} disabled={uploading} className="bg-blue-600 text-white hover:bg-blue-700">
            <Upload size={16} className="mr-2" />
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfilePictureUploadDialog;
